import { useState, useEffect, useContext } from "react";
import AuthContext from "../../../../../utils/secure-route/AuthContext";

import useApi from "../../../../../utils/api-manager/Helper/useApi";
import { useToastHandler } from "../../../../common/ToastHandler";

const UomController = () => {
  const { user } = useContext(AuthContext);
  const storeId = user?.store;
  const [uomDataList, setUomDataList] = useState([]);
  const [uomDataStore, setUomDataStore] = useState([]);
  const [selectedUom, setSelectedUom] = useState("");
  const [selectedUomsList, setSelectedUomsList] = useState([]);
  const [showUomModal, setShowUomModal] = useState(false);
  const [selectedUomsToDelete, setSelectedUomsToDelete] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [lastSearchTerm, setLastSearchTerm] = useState("");
 const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);
  const { Get, Post, Delete, getAPI, _Get , GetPaginatedData } = useApi();
  const { handleToast } = useToastHandler();

  useEffect(() => {
  fetchUomSearch({ page: currentPage });
}, [currentPage]);
  const fetchUomData = async ({ page = 1, searchTerm = "", sort = "" } = {}) => {
    try {
      setLoading(true);

      if (searchTerm && searchTerm.trim().length > 0 && searchTerm.trim().length < 2) {
        setLoading(false);
        return { data: [], totalPages: 1 };
      }
      const response = await GetPaginatedData("allUomData", {
        page,
        search: searchTerm || undefined,
        queryParams: {
          ...(sort && { sort }),
        }
      });

      const results = response?.data || [];
      const count = response?.totalCount || 0;
      const totalPages = Math.ceil(count / 10);
      setUomDataList(results);
      setTotalPages(totalPages);
      setCurrentPage(page);

      return { data: results, totalPages };
    } catch (error) {
      console.error("Error fetching UOM data:", error);
      handleToast({
        type: "ERROR",
        message: "Failed to fetch UOM data.",
      });
      return { data: [], totalPages: 1 };
    } finally {
      setLoading(false);
    }
  };

  const handleUomSelect = (event, newValues) => {
    const validNewValues = newValues.filter((newValue) => {
      const isInStore = uomDataStore.some((item) => item.id === newValue.id);
      if (isInStore) {

        handleToast({
          type: "ERROR",
          message:`${newValue.uom_title} is already added to your store`
        });
        return false;
      }
      return true;
    });
    setSelectedUomsList(validNewValues);
  };


  const handleUomSelectMultiple = (selectedOption) => {
  if (!selectedOption) return;

  // Prevent duplicates in store or already selected list
  const isInStore = uomDataStore.some(uom => uom.id === selectedOption.id);
  if (isInStore) {
    handleToast({ type: "ERROR", message: `${selectedOption.uom_title} is already added to your store` });
    return;
  }

  const isAlreadySelected = selectedUomsList.some(uom => uom.id === selectedOption.id);
  if (isAlreadySelected) {
    handleToast({ type: "ERROR", message: `${selectedOption.uom_title} is already in your selection` });
    return;
  }

  // Add new selection
  setSelectedUomsList(prev => [...prev, selectedOption]);
};
 

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= pageCount) {
      setCurrentPage(pageNumber);
    }
  };

  const pageCount = Math.ceil(totalCount / itemsPerPage);

  const handleCloseUomModal = () => {
    setShowUomModal(false);
    setSelectedUom("");
    handleClearUom();
  };

  const handleUomChange = (e) => {
    const selectedValue = e.target.value;
    if (!selectedValue) return;

    const isInList = selectedUomsList.some((item) => item.id === selectedValue);
    if (isInList) {

      handleToast({
        type: "ERROR",
        message: "This UOM is already in your selection",
      });
      return;
    }

    const isInStore = uomDataStore.some((item) => item.id === selectedValue);
    if (isInStore) {

      handleToast({
        type: "ERROR",
        message: "This UOM is already added to your store",
      });
      return;
    }

    const selectedUomData = uomDataList.find((uom) => uom.id === selectedValue);
    if (selectedUomData) {
      setSelectedUomsList((prev) => [...prev, selectedUomData]);
      setSelectedUom("");
    }
  };

  const handleRemoveFromList = (id) => {
    setSelectedUomsList((prev) => prev.filter((uom) => uom.id !== id));
  };

  const handleAddUom = () => {
    if (!selectedUomsList.length) {
      handleToast({
        type: "ERROR",
        message: "Please select at least one UOM",
      });
      return;
    }

    if (!storeId) {
      handleToast({
        type: "ERROR",
        message: "Store ID is required",
      });
      return;
    }

    const addPromises = selectedUomsList.map((uom) => {
      const payload = { uom: uom.id, store: storeId };
      return Post("uomData", payload);
    });

    return Promise.all(addPromises)
      .then(() => {
        handleToast({
          type: "SUCCESS",
          message: "UOMs added successfully",
        });

        setCurrentPage(1);
      })
    .then(() => {
  handleClearUom();
  fetchUomSearch({ page: 1 }); // 🔥 refresh list
})
    
      .catch((error) => {
        console.error("Error adding UOMs:", error);

        handleToast({
          type: "ERROR",
          message: "Error adding UOMs",
        });
      });
  };

  const handleUomSelectForDeletion = (id) => {
    setSelectedUomsToDelete((prev) =>
      prev.includes(id) ? prev.filter((uomId) => uomId !== id) : [...prev, id]
    );
  };

  const handleDeleteSelectedUoms = () => {
    if (selectedUomsToDelete.length === 0) {
      handleToast({
        type: "ERROR",
        message: "Please select at least one UOM to delete",
      });
      return;
    }

    const itemsBeingDeleted = selectedUomsToDelete.length;
    const remainingItemsOnCurrentPage = uomDataStore.length - itemsBeingDeleted;

    const deletePromises = selectedUomsToDelete.map((id) => Delete("uomData", id));

    Promise.all(deletePromises)
      .then(() => {
        handleToast({
          type: "SUCCESS",
          message: "Selected UOMs deleted successfully",
        });

        let newPage = currentPage;

        if (remainingItemsOnCurrentPage === 0 && currentPage > 1) {
          newPage = currentPage - 1;
        }

        if (currentPage === pageCount && remainingItemsOnCurrentPage === 0) {
          newPage = Math.max(1, currentPage - 1);
        }

        setCurrentPage(newPage);
      })
    .then(() => {
  setSelectedUomsToDelete([]);
  fetchUomSearch({ page: currentPage }); // 🔥 refresh
})
    
      .catch((error) => {
        console.error("Error deleting UOMs:", error);
        handleToast({
          type: "ERROR",
          message: "Error deleting UOMs",
        });
      });
  };

  const handleClearUom = () => {
    setSelectedUom("");
    setSelectedUomsList([]);
    setSelectedUomsToDelete([]);
  };

   const fetchUomSearch = async ({ page = 1, searchTerm = "", sort = "" } = {}) => {
    try {
      setLoading(true);

      if (searchTerm && searchTerm.trim().length > 0 && searchTerm.trim().length < 2) {
        setLoading(false);
        return { data: [], totalPages: 1 };
      }
      const response = await GetPaginatedData("uomData", {
        page,
        search: searchTerm || undefined,
        queryParams: {
          ...(sort && { sort }),
        }
      });

      const results = response?.data || [];
      const count = response?.totalCount || 0;
      const totalPages = Math.ceil(count / 10);
      setUomDataStore(results);
      setTotalPages(totalPages);
      setCurrentPage(page);

      return { data: results, totalPages };
    } catch (error) {
      console.error("Error fetching UOM search data:", error);
      handleToast({
        type: "ERROR",
        message: "Failed to fetch UOM search data.",
      });
      return { data: [], totalPages: 1 };
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term) => {
    setSearchQuery(term);
    setCurrentPage(1); 
  };

  useEffect(() => {
    const trimmed = searchQuery.trim();

    if ((trimmed === "" || trimmed.length >= 2) && trimmed !== lastSearchTerm) {
      // console.log("Making UOM search API call for search term:", trimmed);
      setLastSearchTerm(trimmed);
    } else if (trimmed !== lastSearchTerm) {
      // console.log("Skipping UOM search API call for short search term:", trimmed);
      setLastSearchTerm(trimmed);
    }
  }, [currentPage, searchQuery]);
  return {
    showUomModal,
    setShowUomModal,
    selectedUom,
    handleUomChange,
    handleAddUom,
    uomDataList: uomDataList || [],
    handleCloseUomModal,
    uomDataStore: uomDataStore || [],
    handleClearUom,
    handleRemoveFromList,
    selectedUomsList: selectedUomsList || [],
    selectedUomsToDelete,
    handleUomSelectForDeletion,
    handleDeleteSelectedUoms,
    loading,
    fetchUomData,
    searchQuery,
    setSearchQuery,
    handleSearch,
    handleUomSelect,
    pageCount,
    currentPage,
    totalCount,
    handlePageChange,
    setSelectedUom,
    handleUomSelectMultiple
  };
};

export default UomController;